import React, { useMemo } from 'react';
import { View, Text, Pressable, Platform, useWindowDimensions } from 'react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { BaseModal } from '../../ui/BaseModal/BaseModal';
import { CountryFlag } from '../CountryFlag/CountryFlag';
import { FriendAvatar } from '../../friends/shared/FriendAvatar';
import { getFinishedScoreDisplay } from '../MatchesView/matchCardFinishedScore.helper';
import { getTeamKitConfig } from '../../../helpers/teamKit.helper';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import type { DisplayAvatarId } from '../../../src/avatars/types/avatar.types';
import type { EquippedMap } from '../../avatars/LayeredAvatarLab/characterCustomizer.types';
import type { PostGameResultMatch } from '../../../src/worldcup/hooks/usePostGameResultOverlay';
import { getPostGameResultOverlayStyles } from './PostGameResultOverlay.styles';
import {
  POST_GAME_COPY,
  resolveHeadline,
} from './postGameResultOverlay.constants';

export interface PostGameResultOverlayProps {
  isVisible: boolean;
  result: PostGameResultMatch | null;
  /** Preset avatar id of the active user (live AvatarContext value preferred by callers). */
  avatarId?: DisplayAvatarId | null;
  /** Custom layered look; overrides avatarId when set. */
  avatarConfig?: EquippedMap | null;
  /** Legacy profile-picture URL fallback when no Zporti avatar exists. */
  avatarFallbackUrl?: string;
  onDismiss: () => void;
}

export const PostGameResultOverlay: React.FC<PostGameResultOverlayProps> = ({
  isVisible,
  result,
  avatarId,
  avatarConfig,
  avatarFallbackUrl,
  onDismiss,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getPostGameResultOverlayStyles(theme), [theme]);
  const { width: windowWidth } = useWindowDimensions();

  // Cap the card to the actual viewport so it never overflows on narrow (mobile
  // browser) widths. Percentage maxWidth can't be trusted here: inside BaseModal
  // the content container shrink-wraps to the card, so `%` resolves against the
  // card itself rather than the screen.
  const cardWidth = Math.min(
    Platform.OS === 'web' ? 480 : 320,
    windowWidth - 32
  );

  if (!result) return null;

  const { match, isExactHit, isCorrectResult, earnedPoints, isBoosted } = result;
  const scoreDisplay = getFinishedScoreDisplay(match);
  const homeKitFallback = getTeamKitConfig(match.homeTeam);
  const awayKitFallback = getTeamKitConfig(match.awayTeam);

  const headline = resolveHeadline(isExactHit, isCorrectResult);
  // NAILED_IT and NICE_CATCH are both wins (green); a miss is neutral (not red — it's not a loss).
  const headlineStyle =
    headline === POST_GAME_COPY.NEXT_TIME ? styles.headlineMiss : styles.headlineWon;

  const badgeStyle = isExactHit
    ? styles.outcomeBadgeHit
    : isCorrectResult
    ? styles.outcomeBadgeResult
    : styles.outcomeBadgeMiss;

  const badgeText = isExactHit
    ? POST_GAME_COPY.BADGE_EXACT
    : isCorrectResult
    ? POST_GAME_COPY.BADGE_RESULT
    : POST_GAME_COPY.BADGE_MISS;

  const pointsPositive = earnedPoints > 0;

  return (
    <BaseModal
      isVisible={isVisible}
      onDismiss={onDismiss}
      dismissible={false}
      testID="post-game-result-overlay"
    >
      <View style={[styles.card, { width: cardWidth }]}>
          {/* Avatar — hidden entirely when the user has nothing to show (no initials placeholder) */}
          {(avatarConfig || avatarId || avatarFallbackUrl) && (
            <View style={styles.avatarWrap}>
              <FriendAvatar
                avatarId={avatarId}
                avatarConfig={avatarConfig}
                fallbackUrl={avatarFallbackUrl}
                size={140}
                avatarRounded
                testID="post-game-result-avatar"
              />
            </View>
          )}

          {/* Headline */}
          <Text style={[styles.headline, headlineStyle]}>{headline}</Text>

          {/* Outcome badge (+ prominent boost indicator when x2 was active) */}
          <View style={styles.badgeRow}>
            <View style={[styles.outcomeBadge, badgeStyle]}>
              <Text style={styles.outcomeBadgeText}>{badgeText}</Text>
            </View>
            {isBoosted && (
              <View style={styles.boostBadge}>
                <Text style={styles.boostBadgeText}>⚡ ×2</Text>
              </View>
            )}
          </View>

          {/* Match row */}
          <View style={styles.matchRow}>
            <View style={styles.teamBlock}>
              <View style={styles.flagWrap}>
                <CountryFlag
                  countryCode={match.homeCountryCode}
                  kitFallback={homeKitFallback}
                  accessibilityLabel={`${match.homeTeam} flag`}
                />
              </View>
              <Text style={styles.teamName} numberOfLines={2}>
                {match.homeTeam}
              </Text>
            </View>

            <View style={styles.scoreBlock}>
              {scoreDisplay.kind === 'single' ? (
                <View style={styles.scoreRow}>
                  <Text style={styles.scoreNum}>{scoreDisplay.home}</Text>
                  <Text style={styles.scoreDash}>–</Text>
                  <Text style={styles.scoreNum}>{scoreDisplay.away}</Text>
                </View>
              ) : (
                <>
                  <View style={styles.scoreRow}>
                    <Text style={styles.scoreNum}>{scoreDisplay.final.home}</Text>
                    <Text style={styles.scoreDash}>–</Text>
                    <Text style={styles.scoreNum}>{scoreDisplay.final.away}</Text>
                  </View>
                  {scoreDisplay.penalty && (
                    <Text style={styles.scorePens}>
                      {`${scoreDisplay.penalty.home}–${scoreDisplay.penalty.away} pens`}
                    </Text>
                  )}
                  <Text style={styles.scoreRegLabel}>
                    {`90 min: ${scoreDisplay.regulation.home}–${scoreDisplay.regulation.away}`}
                  </Text>
                </>
              )}
            </View>

            <View style={styles.teamBlock}>
              <View style={styles.flagWrap}>
                <CountryFlag
                  countryCode={match.awayCountryCode}
                  kitFallback={awayKitFallback}
                  accessibilityLabel={`${match.awayTeam} flag`}
                />
              </View>
              <Text style={styles.teamName} numberOfLines={2}>
                {match.awayTeam}
              </Text>
            </View>
          </View>

          <View style={styles.divider} />

          {/* Points */}
          <View style={styles.pointsRow}>
            <Text style={styles.pointsLabel}>Points earned:</Text>
            <Text
              style={[
                styles.pointsValue,
                pointsPositive ? styles.pointsValuePos : styles.pointsValueNeg,
              ]}
            >
              {pointsPositive ? `+${earnedPoints}` : '+0'}
            </Text>
          </View>

          {/* Dismiss */}
          <Pressable
            onPress={onDismiss}
            style={({ pressed }) => [
              styles.dismissButton,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.dismissButtonPressed,
            ]}
            accessibilityRole="button"
            accessibilityLabel={POST_GAME_COPY.DISMISS}
          >
            <Text style={styles.dismissLabel}>{POST_GAME_COPY.DISMISS}</Text>
          </Pressable>
        </View>
    </BaseModal>
  );
};
